import React, { useState } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';

interface FooterLink { label: string; url: string; }
interface FooterColumn { title: string; links: FooterLink[]; }
interface SocialLink { platform: string; url: string; }

const SOCIAL_ICONS: Record<string, string> = {
  twitter:   '𝕏',
  x:         '𝕏',
  facebook:  'f',
  instagram: '◉',
  linkedin:  'in',
  youtube:   '▶',
  github:    '⌥',
  tiktok:   '♪',
};

const FooterMultiColumn: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const columns = (c.columns || []) as FooterColumn[];
  const socialLinks = (c.social_links || []) as SocialLink[];
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  return (
    <footer
      className="sb-render sb-render-footer-multi-column"
      style={{ background: '#0f172a', color: '#cbd5e1', padding: '64px 24px 32px' }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Top row: brand + columns */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `2fr ${columns.map(() => '1fr').join(' ')}`,
          gap: 48,
          marginBottom: 48,
          flexWrap: 'wrap',
        }}>
          {/* Brand column */}
          <div>
            <InlineEditText
              tag="div"
              controlKey="brand_name"
              style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff', marginBottom: 12 }}
            >
              {c.brand_name || 'Brand Name'}
            </InlineEditText>
            <InlineEditText
              tag="p"
              controlKey="tagline"
              style={{ fontSize: '0.9rem', lineHeight: 1.6, marginBottom: 24, color: '#94a3b8' }}
            >
              {c.tagline || 'Your tagline here.'}
            </InlineEditText>

            {/* Social links */}
            {socialLinks.length > 0 && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {socialLinks.map((s, i) => (
                  <a
                    key={i}
                    href={s.url || '#'}
                    aria-label={s.platform}
                    title={s.platform}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: '#1e293b',
                      color: '#94a3b8',
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      transition: 'background 0.2s, color 0.2s',
                    }}
                  >
                    {SOCIAL_ICONS[s.platform?.toLowerCase()] || s.platform?.charAt(0)?.toUpperCase() || '?'}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Link columns */}
          {columns.map((col, ci) => (
            <div key={ci}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#fff', marginBottom: 16 }}>
                {col.title || `Column ${ci + 1}`}
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(col.links || []).map((lnk, li) => (
                  <li key={li}>
                    <a href={lnk.url || '#'} style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.9rem', transition: 'color 0.2s' }}>
                      {lnk.label || 'Link'}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        {c.newsletter_enabled !== false && (
          <div style={{
            borderTop: '1px solid #1e293b',
            borderBottom: '1px solid #1e293b',
            padding: '32px 0',
            marginBottom: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 24,
            flexWrap: 'wrap',
          }}>
            <div style={{ flex: '1 1 300px' }}>
              <p style={{ fontWeight: 700, color: '#fff', marginBottom: 4 }}>Stay in the loop</p>
              <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>Get updates delivered to your inbox.</p>
            </div>
            {submitted ? (
              <p style={{ color: '#4ade80', fontWeight: 600 }}>✓ Thanks for subscribing!</p>
            ) : (
              <form
                onSubmit={e => { e.preventDefault(); setSubmitted(true); }}
                style={{ display: 'flex', gap: 8, flex: '1 1 300px' }}
              >
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    borderRadius: 'var(--brand-btn-radius, 6px)',
                    border: '1px solid #334155',
                    background: '#1e293b',
                    color: '#fff',
                    fontSize: '0.9rem',
                    outline: 'none',
                  }}
                />
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    borderRadius: 'var(--brand-btn-radius, 6px)',
                    background: 'var(--brand-primary, #2563eb)',
                    color: '#fff',
                    border: 'none',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Subscribe
                </button>
              </form>
            )}
          </div>
        )}

        {/* Copyright */}
        <div style={{ textAlign: 'center', fontSize: '0.8rem', color: '#475569' }}>
          <InlineEditText tag="span" controlKey="copyright_text">
            {c.copyright_text || `© ${new Date().getFullYear()} ${c.brand_name || 'Company'}. All rights reserved.`}
          </InlineEditText>
        </div>
      </div>
    </footer>
  );
};

export default FooterMultiColumn;
